import { Payer } from "@redotech/cockroachdb-codegen/congodb/payer";
import { t } from "@redotech/redo-i18n";
import { ShipmentMethodName } from "@redotech/redo-return-core-common/draft-return/draft-return-enums";
import { returnOptionMethodJsonFormat } from "@redotech/redo-return-core-common/return-flow/return-option";
import { getChargeableShipmentFee } from "@redotech/redo-return-core-common/shipping/return-carrier-option";
import { areAllDraftReturnShipmentsBlueYonderEligible } from "@redotech/redo-return-operations-common/draft-return/blue-yonder-eligibility";
import { DraftReturn } from "@redotech/redo-return-operations-common/draft-return/draft-return";
import { merchantFlowShortcutsEnabled } from "@redotech/redo-return-operations-common/draft-return/merchant-claim-mode";
import {
  getCompensationMethod,
  hasReturnItemsThatRequireShipping,
  returnOptionMethodMap,
} from "@redotech/redo-return-operations-common/draft-return/util";
import { Big } from "big.js";
import { ShippingEstimateData } from "../../contexts/shipping-estimate";
import { CustomerPortalAppSettings } from "../../settings";
import { ShipmentMethodOption } from "../../shipment-method/util";

export const methodsToDisplay = [
  ShipmentMethodName.IN_STORE,
  ShipmentMethodName.SELF_SHIP,
  ShipmentMethodName.BLUE_YONDER,
  ShipmentMethodName.MANUAL_LABEL,
  ShipmentMethodName.DROPOFF,
] as const;

export type MethodToDisplay = (typeof methodsToDisplay)[number];

export const isMethodToDisplay = (
  type: ShipmentMethodName,
): type is MethodToDisplay => {
  for (const method of methodsToDisplay) {
    if (method === type) {
      return true;
    }
  }
  return false;
};

export const getShippingMethodOptions = async (
  draftReturn: DraftReturn,
  shippingEstimateContext: ShippingEstimateData,
  settings: CustomerPortalAppSettings | undefined,
) => {
  // If no shipping is required, then skip all shipping methods
  if (!hasReturnItemsThatRequireShipping(draftReturn)) {
    console.log("shipmentMethodOptions: No shipping required");
    return [];
  }
  const compensationMethod = getCompensationMethod(draftReturn);

  const shippingMethodOptions: ShipmentMethodOption[] = [];

  const shipments = await shippingEstimateContext.getShipments();
  const totalShipmentFee = (await shipments).reduce((acc, shipment) => {
    return acc.add(getChargeableShipmentFee(shipment).fee);
  }, new Big(0));

  // Calculate customer up-charge fee separately for UI display
  const customerUpchargeFee = (await shipments).reduce((acc, shipment) => {
    if (shipment.customerUpchargeFee) {
      return acc.add(shipment.customerUpchargeFee);
    }
    return acc;
  }, new Big(0));

  const labelPayer = compensationMethod?.payer;

  const allDropoffLocations = shipments.flatMap(
    (shipment) => shipment.dropoffs ?? [],
  );

  // Generic dropoff option — shown when any shipment has dropoff locations
  if (allDropoffLocations.length > 0) {
    shippingMethodOptions.push({
      type: ShipmentMethodName.DROPOFF,
      nonPickupFee: new Big(0),
      dropoffs: allDropoffLocations,
    });
  }

  // Check if return in store is available
  const inStoreLocations = shipments.find(
    (shipment) => !!shipment.inStoreLocations,
  )?.inStoreLocations;
  if (inStoreLocations) {
    //If no distances exist, still show in store
    shippingMethodOptions.push({
      type: ShipmentMethodName.IN_STORE,
      inStoreLocations,
    });
  }

  if (
    draftReturn.pendingReturnItems.some((item) =>
      [...item.returnOptions].find((option) => {
        const optionMethod = returnOptionMethodJsonFormat.read(
          option.method,
        ).type;
        const compensationMethodType = compensationMethod?.type
          ? returnOptionMethodMap[compensationMethod.type]
          : undefined;
        const isManualLabel = option.manual_label === true;
        return optionMethod === compensationMethodType && isManualLabel;
      }),
    )
  ) {
    shippingMethodOptions.push({ type: ShipmentMethodName.MANUAL_LABEL });
    return shippingMethodOptions;
  }

  //Happy returns
  // if (
  //   shipments.length > 0 &&
  //   shipments.every((shipment) => !!shipment.happyReturnsDetails)
  // ) {
  //   const totalHappyReturnsFee = shipments.reduce((acc, shipment) => {
  //     return acc.add(shipment.happyReturnsDetails!.price);
  //   }, new Big(0));

  //   shippingMethodOptions.push({
  //     type: ShipmentMethodName.HAPPY_RETURNS,
  //     nonPickupFee:
  //       labelPayer === Payer.Customer ? totalHappyReturnsFee : new Big(0),
  //     happyReturnsDetails: {
  //       ...shipments[0]!.happyReturnsDetails!,
  //     },
  //   });

  //   shippingMethodOptions.push({
  //     type: ShipmentMethodName.SELF_SHIP,
  //     nonPickupFee:
  //       labelPayer === Payer.Customer ? totalShipmentFee : new Big(0),
  //   });
  //   return shippingMethodOptions;
  // }

  // BlueYonder returns - only show if no generic dropoff locations are available
  if (
    allDropoffLocations.length === 0 &&
    areAllDraftReturnShipmentsBlueYonderEligible(shipments)
  ) {
    shippingMethodOptions.push({
      type: ShipmentMethodName.BLUE_YONDER,
      nonPickupFee: new Big(0), // BlueYonder drop-off is typically free
      blueYonderDetails: {
        locations: shipments[0]!.blueYonderDetails!.locations,
        retailerCompanyId: shipments[0]!.blueYonderDetails!.retailerCompanyId,
        privacyPolicyText: t(
          "By selecting you are agreeing to our privacy policy.",
        ),
      },
    });
  }

  const pickupFee =
    shipments?.length === 1 && shipments?.[0]?.pickupFee
      ? shipments[0].pickupFee
      : undefined;

  const pickupCoveredByMerchant = shipments[0]?.pickupCoveredByMerchant;

  if (pickupFee && !merchantFlowShortcutsEnabled(draftReturn)) {
    shippingMethodOptions.push({
      type: ShipmentMethodName.PICKUP,
      pickupFee: new Big(pickupFee),
      pickupCoveredByMerchant: pickupCoveredByMerchant ?? undefined,
      nonPickupFee:
        labelPayer === Payer.Customer ? totalShipmentFee : new Big(0),
    });
  }

  // Determine if self-ship will be the only option available
  const willBeOnlyOption = shippingMethodOptions.length === 0;

  // Apply upcharge fee based on settings
  let upchargeFeeToApply: Big | undefined;
  if (customerUpchargeFee.gt(0)) {
    const dontChargeWhenOnlyOption =
      settings?.dontChargeCustomerLabelUpchargeWhenOnlyOption || false;
    if (!dontChargeWhenOnlyOption || !willBeOnlyOption) {
      upchargeFeeToApply = customerUpchargeFee;
    }
  }

  shippingMethodOptions.push({
    type: ShipmentMethodName.SELF_SHIP,
    nonPickupFee: labelPayer === Payer.Customer ? totalShipmentFee : new Big(0),
    customerUpchargeFee: upchargeFeeToApply,
  });

  // Check if settlement is available
  // if (
  //   settings?.returns?.settlementEnabled &&
  //   settings?.returns?.settlementRefund &&
  //   settings?.returns?.settlementRefund > 0
  // ) {
  //   const orderValue = getCompensationMethod(
  //     returnContext.draftReturn,
  //   )?.totalReturnableValue;
  //   if (orderValue?.gt(0)) {
  //     shippingMethodOptions.push({ type: ShipmentMethodName.SETTLEMENT, orderValue });
  //   }
  // }
  return shippingMethodOptions;
};
